import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import AccountInput from "./AccountInput";
import {
  createPatientCard,
  generateQRCode,
  uploadImage,
} from "./api/apiFunctions";
import "./datepicker-theme.css";
import { t } from "./i18n";

const DateInput = React.forwardRef(
  ({ value, onClick, onChange, placeholder }, ref) => (
    <div className="relative flex flex-col mb-4 flex-1 min-w-[180px]">
      <input
        ref={ref}
        value={value}
        onClick={onClick}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border rounded p-2 text-sm"
        autoComplete="off"
      />
      <span
        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center cursor-pointer"
        onClick={onClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#fff" />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            stroke="#4962d8"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    </div>
  ),
);

export default function CreateCardModal({
  open,
  onClose,
  onSubmit,
  lang = "en",
}) {
  const [form, setForm] = useState({
    allergies: [],
    medications: [],
    conditions: [],
    hospitals: [], // Will now store objects: { name: string, city: string, zipCode: string }
    doctors: [], // Will now store objects: { name: string, status: 'current' | 'past' }
    contact: "",
    religion: "",
    organDonor: false,
    isSmoker: false,
    bloodType: "",
    dob: "",
    ssn: "",
    ekg: "",
    primaryInsurance: [],
    secondaryInsurance: [],
    primaryInsuranceMemberId: "",
    secondaryInsuranceMemberId: "",
    insuranceCard: "",
    otherBloodType: "",
    primaryPhone: "",
    secondaryPhone: "",
    secondaryMobile: "",
    // Missing fields from the API
    primaryPhoneNumber: "",
    secondaryPhoneNumber: "",
    secondaryMobileNumber: "",
  });

  // Search states for hospital and doctor
  const [hospitalSearch, setHospitalSearch] = useState("");
  const [doctorSearch, setDoctorSearch] = useState("");
  const [hospitalResults, setHospitalResults] = useState([]);
  const [doctorResults, setDoctorResults] = useState([]);
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [isHospitalLoading, setIsHospitalLoading] = useState(false);
  const [isDoctorLoading, setIsDoctorLoading] = useState(false);
  const hospitalDropdownRef = useRef(null);
  const doctorDropdownRef = useRef(null);

  // Search states for conditions and medications
  const [conditionSearch, setConditionSearch] = useState("");
  const [medicationSearch, setMedicationSearch] = useState("");
  const [conditionResults, setConditionResults] = useState([]);
  const [medicationResults, setMedicationResults] = useState([]);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [isConditionLoading, setIsConditionLoading] = useState(false);
  const [isMedicationLoading, setIsMedicationLoading] = useState(false);
  const conditionDropdownRef = useRef(null);
  const medicationDropdownRef = useRef(null);

  // Temporary input states for manual entry
  const [tempAllergy, setTempAllergy] = useState("");
  // const [isNoAllergies, setIsNoAllergies] = useState(false);
  const [tempMedication, setTempMedication] = useState("");
  const [tempCondition, setTempCondition] = useState("");
  const [tempHospital, setTempHospital] = useState("");
  const [tempDoctor, setTempDoctor] = useState("");
  const [tempDoctorStatus, setTempDoctorStatus] = useState("current"); // New state for doctor status
  const [tempDoctorSpecialization, setTempDoctorSpecialization] =
    useState("General Practice");
  const [selectedPrimaryInsurance, setSelectedPrimaryInsurance] = useState("");
  const [selectedSecondaryInsurance, setSelectedSecondaryInsurance] =
    useState("");
  const [otherPrimaryInsurance, setOtherPrimaryInsurance] = useState("");
  const [otherSecondaryInsurance, setOtherSecondaryInsurance] = useState("");
  const [tempBloodType, setTempBloodType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ekgFileName, setEkgFileName] = useState("");
  const [insuranceCardFileName, setInsuranceCardFileName] = useState("");
  const [errors, setErrors] = useState({
    primaryPhone: "",
    bloodType: "",
    dob: "",
    ssn: "",
    allergies: "",
    medications: "",
    conditions: "",
    hospitals: "",
    doctors: "",
    secondaryPhone: "",
    secondaryMobile: "",
    religion: "",
    ekg: "",
    insuranceCard: "",
  });

  // Handle clicks outside dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        hospitalDropdownRef.current &&
        !hospitalDropdownRef.current.contains(event.target)
      ) {
        setShowHospitalDropdown(false);
      }
      if (
        doctorDropdownRef.current &&
        !doctorDropdownRef.current.contains(event.target)
      ) {
        setShowDoctorDropdown(false);
      }
      if (
        conditionDropdownRef.current &&
        !conditionDropdownRef.current.contains(event.target)
      ) {
        setShowConditionDropdown(false);
      }
      if (
        medicationDropdownRef.current &&
        !medicationDropdownRef.current.contains(event.target)
      ) {
        setShowMedicationDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch hospitals from API
  useEffect(() => {
    const fetchHospitals = async () => {
      if (!hospitalSearch || hospitalSearch.length < 2) {
        setHospitalResults([]);
        setIsHospitalLoading(false);
        return;
      }

      setIsHospitalLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/npi_org/v3/search?terms=${encodeURIComponent(
          hospitalSearch,
        )}&count=5&df=NPI,name.full,provider_type,addr_practice.full,addr_practice.phone`;
        const res = await fetch(url);
        const data = await res.json();
        const results = (data[3] || []).map((row, idx) => ({
          npi: data[1][idx],
          name: row[1] || "",
          specialty: row[2] || "",
          address: row[3] || "",
          phone: row[4] || "",
        }));
        setHospitalResults(results);
        // Keep dropdown open even if no results, so "no results" message can show
        setShowHospitalDropdown(true);
      } catch (e) {
        setHospitalResults([]);
        // Keep dropdown open even on error, so "no results" message can show
        setShowHospitalDropdown(true);
      } finally {
        setIsHospitalLoading(false);
      }
    };
    const timeout = setTimeout(fetchHospitals, 400);
    return () => clearTimeout(timeout);
  }, [hospitalSearch]);

  // Fetch doctors from API
  useEffect(() => {
    const fetchDoctors = async () => {
      if (!doctorSearch || doctorSearch.length < 2) {
        setDoctorResults([]);
        setIsDoctorLoading(false);
        return;
      }

      setIsDoctorLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search?terms=${encodeURIComponent(
          doctorSearch,
        )}&count=5&df=NPI,name.full,provider_type,gender,name.credential,addr_practice.full,addr_practice.phone`;
        const res = await fetch(url);
        const data = await res.json();
        const results = (data[3] || []).map((row, idx) => ({
          npi: data[1][idx],
          name: row[1] || "",
          specialty: row[2] || "",
          address: row[3] || "",
        }));
        setDoctorResults(results);
        // Keep dropdown open even if no results, so "no results" message can show
        setShowDoctorDropdown(true);
      } catch (e) {
        setDoctorResults([]);
        // Keep dropdown open even on error, so "no results" message can show
        setShowDoctorDropdown(true);
      } finally {
        setIsDoctorLoading(false);
      }
    };
    const timeout = setTimeout(fetchDoctors, 400);
    return () => clearTimeout(timeout);
  }, [doctorSearch]);

  // Fetch medical conditions from NLM API
  useEffect(() => {
    const fetchConditions = async () => {
      if (!conditionSearch || conditionSearch.length < 2) {
        setConditionResults([]);
        setIsConditionLoading(false);
        return;
      }

      setIsConditionLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/conditions/v3/search?terms=${encodeURIComponent(
          conditionSearch,
        )}&count=5&df=consumer_name,primary_name&ef=icd10cm_codes,term_icd9_code,term_icd9_text`;
        const res = await fetch(url);
        const data = await res.json();

        const results = (data[3] || []).map((row, idx) => ({
          consumerName: row[0] || "",
          primaryName: row[1] || "",
          icd10cmCodes: data[2]?.icd10cm_codes?.[idx] || "",
          icd9Code: data[2]?.term_icd9_code?.[idx] || "",
          icd9Text: data[2]?.term_icd9_text?.[idx] || "",
        }));

        setConditionResults(results);
        setShowConditionDropdown(true);
      } catch (e) {
        console.error("Error fetching conditions:", e);
        setConditionResults([]);
        setShowConditionDropdown(true);
      } finally {
        setIsConditionLoading(false);
      }
    };
    const timeout = setTimeout(fetchConditions, 400);
    return () => clearTimeout(timeout);
  }, [conditionSearch]);

  // Fetch medications from RxTerms API
  useEffect(() => {
    const fetchMedications = async () => {
      if (!medicationSearch || medicationSearch.length < 2) {
        setMedicationResults([]);
        setIsMedicationLoading(false);
        return;
      }

      setIsMedicationLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search?terms=${encodeURIComponent(
          medicationSearch,
        )}&count=5&df=DISPLAY_NAME&ef=STRENGTHS_AND_FORMS,RXCUIS`;
        const res = await fetch(url);
        const data = await res.json();

        const results = (data[3] || []).map((row, idx) => ({
          displayName: row[0] || "",
          strengthsAndForms: data[2]?.STRENGTHS_AND_FORMS?.[idx] || [],
          rxcuis: data[2]?.RXCUIS?.[idx] || [],
        }));

        setMedicationResults(results);
        setShowMedicationDropdown(true);
      } catch (e) {
        console.error("Error fetching medications:", e);
        setMedicationResults([]);
        setShowMedicationDropdown(true);
      } finally {
        setIsMedicationLoading(false);
      }
    };
    const timeout = setTimeout(fetchMedications, 400);
    return () => clearTimeout(timeout);
  }, [medicationSearch]);

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setEkgFileName("");
      setInsuranceCardFileName("");
      setErrors({
        primaryPhone: "",
        bloodType: "",
        dob: "",
        ssn: "",
        allergies: "",
        medications: "",
        conditions: "",
        hospitals: "",
        doctors: "",
        secondaryPhone: "",
        secondaryMobile: "",
        religion: "",
        ekg: "",
        insuranceCard: "",
      });
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // DatePicker change handler
  const handleDateChange = (date) => {
    setForm((prev) => ({
      ...prev,
      dob: date
        ? `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
            date.getDate(),
          ).padStart(2, "0")}/${date.getFullYear()}`
        : "",
    }));
  };

  const handleSSNChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, "");

    // Format as XXX-XX-XXXX
    if (numbers.length <= 3) {
      formattedValue = numbers;
    } else if (numbers.length <= 5) {
      formattedValue = numbers.slice(0, 3) + "-" + numbers.slice(3);
    } else if (numbers.length <= 9) {
      formattedValue =
        numbers.slice(0, 3) +
        "-" +
        numbers.slice(3, 5) +
        "-" +
        numbers.slice(5, 9);
    } else {
      formattedValue =
        numbers.slice(0, 3) +
        "-" +
        numbers.slice(3, 5) +
        "-" +
        numbers.slice(5, 9);
    }

    setForm((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  // Handler for phone number formatting (supports both US and international formats)
  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Check if it starts with + (international format)
    if (value.startsWith("+")) {
      // International format: allow + and digits, format with dashes for readability
      // Remove everything except + and digits
      const cleaned = value.replace(/[^\d+]/g, "");

      // If it starts with +, keep the + and format the rest
      if (cleaned.startsWith("+")) {
        const digits = cleaned.slice(1); // Remove the +

        // Format: +XX-XXX-XXXXXXX (country code - area - number)
        // For example: +92-310-1030094
        if (digits.length === 0) {
          formattedValue = "+";
        } else if (digits.length <= 2) {
          formattedValue = "+" + digits;
        } else if (digits.length <= 5) {
          formattedValue = "+" + digits.slice(0, 2) + "-" + digits.slice(2);
        } else {
          // Format as +XX-XXX-XXXXXXX (no limit on length for international)
          formattedValue =
            "+" +
            digits.slice(0, 2) +
            "-" +
            digits.slice(2, 5) +
            "-" +
            digits.slice(5);
        }
      } else {
        formattedValue = cleaned;
      }
    } else {
      // US format: XXX-XXX-XXXX (10 digits)
      // Remove all non-numeric characters
      const numbers = value.replace(/\D/g, "");

      // Format as XXX-XXX-XXXX
      if (numbers.length <= 3) {
        formattedValue = numbers;
      } else if (numbers.length <= 6) {
        formattedValue = numbers.slice(0, 3) + "-" + numbers.slice(3);
      } else if (numbers.length <= 10) {
        formattedValue =
          numbers.slice(0, 3) +
          "-" +
          numbers.slice(3, 6) +
          "-" +
          numbers.slice(6, 10);
      } else {
        // Limit to 10 digits for US format
        formattedValue =
          numbers.slice(0, 3) +
          "-" +
          numbers.slice(3, 6) +
          "-" +
          numbers.slice(6, 10);
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  // Handler for EKG file upload (multipart/form-data)
  const handleEkgFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 10 * 1024 * 1024;
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          ekg: "Please upload a (JPEG, PNG, GIF, WebP) file.",
        }));
        e.target.value = "";
        return;
      }
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          ekg: "File size must be less than 10MB",
        }));
        e.target.value = "";
        return;
      }
      setErrors((prev) => ({ ...prev, ekg: "" }));
      setEkgFileName(file.name);
      try {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        if (!token)
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        const response = await uploadImage(file, token);
        toast.success(response?.message || "EKG file uploaded successfully");
        setForm((prev) => ({ ...prev, ekg: response?.fileUrl }));
      } catch (err) {
        console.log("Error uploading image", err);
        setErrors((prev) => ({
          ...prev,
          ekg: "Error uploading file. Please try again.",
        }));
        setEkgFileName("");
        e.target.value = "";
      }
    }
  };

  const handleRemoveEkgFile = () => {
    setForm((prev) => ({ ...prev, ekg: "" }));
    setEkgFileName("");
    const fileInput = document.querySelector('input[name="ekg"]');
    if (fileInput) fileInput.value = "";
  };

  // Handler for Insurance Card file upload (multipart/form-data)
  const handleInsuranceCardFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 10 * 1024 * 1024;
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          insuranceCard:
            "Please upload a image file (JPEG, PNG, GIF, WebP) of this format.",
        }));
        e.target.value = "";
        return;
      }
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          insuranceCard: "File size must be less than 10MB",
        }));
        e.target.value = "";
        return;
      }
      setErrors((prev) => ({ ...prev, insuranceCard: "" }));
      setInsuranceCardFileName(file.name);
      try {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        if (!token)
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        const response = await uploadImage(file, token);
        toast.success(
          response?.message || "Insurance Card file uploaded successfully",
        );
        setForm((prev) => ({ ...prev, insuranceCard: response?.fileUrl }));
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          insuranceCard: "Error uploading file. Please try again.",
        }));
        setInsuranceCardFileName("");
        e.target.value = "";
      }
    }
  };

  // Handler to remove Insurance Card file
  const handleRemoveInsuranceCardFile = () => {
    setForm((prev) => ({ ...prev, insuranceCard: "" }));
    setInsuranceCardFileName("");
    const fileInput = document.querySelector('input[name="insuranceCard"]');
    if (fileInput) fileInput.value = "";
  };

  // Functions to add items to arrays
  const addAllergy = () => {
    if (tempAllergy.trim()) {
      setForm((prev) => ({
        ...prev,
        allergies: [...prev.allergies, tempAllergy.trim()],
      }));
      setTempAllergy("");
    }
  };

  const addMedication = () => {
    if (tempMedication.trim()) {
      setForm((prev) => ({
        ...prev,
        medications: [...prev.medications, tempMedication.trim()],
      }));
      setTempMedication("");
    }
  };

  const addCondition = () => {
    if (tempCondition.trim()) {
      setForm((prev) => ({
        ...prev,
        conditions: [...prev.conditions, tempCondition.trim()],
      }));
      setTempCondition("");
    }
  };

  const addHospital = () => {
    if (tempHospital.trim()) {
      const newHospital = {
        name: tempHospital.trim(),
        city: form.tempHospitalCity?.trim() || "",
        zipCode: form.tempHospitalZip?.trim() || "",
      };
      setForm((prev) => ({
        ...prev,
        hospitals: [...prev.hospitals, newHospital],
        tempHospitalCity: "",
        tempHospitalZip: "",
      }));
      setTempHospital("");
    }
  };

  const addDoctor = () => {
    if (tempDoctor.trim()) {
      setForm((prev) => ({
        ...prev,
        doctors: [
          ...prev.doctors,
          {
            name: tempDoctor.trim(),
            status: tempDoctorStatus,
            specialization: tempDoctorSpecialization,
          },
        ],
      }));
      setTempDoctor("");
      setTempDoctorStatus("current"); // Reset status after adding
      setTempDoctorSpecialization("General Practice"); // Reset specialization after adding
    }
  };

  const handlePrimaryInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedPrimaryInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        primaryInsurance: [],
      }));
    } else if (value) {
      const selectedOption = insuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        primaryInsurance: [selectedOption.label],
      }));
      setOtherPrimaryInsurance("");
    }
  };

  const handleSecondaryInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedSecondaryInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        secondaryInsurance: [],
      }));
    } else if (value) {
      const selectedOption = secondaryInsuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        secondaryInsurance: [selectedOption.label],
      }));
      setOtherSecondaryInsurance("");
    }
  };

  const handleOtherPrimaryInsurance = (e) => {
    const value = e.target.value;
    setOtherPrimaryInsurance(value);
    setForm((prev) => ({
      ...prev,
      primaryInsurance: value.trim() ? [value] : [],
    }));
  };

  const handleOtherSecondaryInsurance = (e) => {
    const value = e.target.value;
    setOtherSecondaryInsurance(value);
    setForm((prev) => ({
      ...prev,
      secondaryInsurance: value.trim() ? [value] : [],
    }));
  };

  const addBloodType = () => {
    if (tempBloodType.trim()) {
      setForm((prev) => ({
        ...prev,
        otherBloodType: tempBloodType.trim(),
      }));
      setTempBloodType("");
    }
  };

  // Functions to remove items from arrays
  const removeAllergy = (index) => {
    setForm((prev) => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index),
    }));
    // // If "None" is being removed, uncheck the checkbox
    // const allergyToRemove = form.allergies[index];
    // if (allergyToRemove === "None") {
    //   setIsNoAllergies(false);
    //   setTempAllergy("");
    // }
  };

  const removeMedication = (index) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const removeCondition = (index) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const removeHospital = (index) => {
    setForm((prev) => ({
      ...prev,
      hospitals: prev.hospitals.filter((_, i) => i !== index),
    }));
  };

  const removeDoctor = (index) => {
    setForm((prev) => ({
      ...prev,
      doctors: prev.doctors.filter((_, i) => i !== index),
    }));
  };

  const toggleDoctorStatus = (index) => {
    setForm((prev) => ({
      ...prev,
      doctors: prev.doctors.map((doctor, i) =>
        i === index
          ? {
              ...doctor,
              status: doctor.status === "current" ? "past" : "current",
            }
          : doctor,
      ),
    }));
  };

  // const removePrimaryInsurance = (index) => {
  //   setForm((prev) => ({
  //     ...prev,
  //     primaryInsurance: prev.primaryInsurance.filter((_, i) => i !== index),
  //   }));
  // };

  // const removeSecondaryInsurance = (index) => {
  //   setForm((prev) => ({
  //     ...prev,
  //     secondaryInsurance: prev.secondaryInsurance.filter((_, i) => i !== index),
  //   }));
  // };

  const handleHospitalSearchChange = (e) => {
    setHospitalSearch(e.target.value);
    // Close other dropdowns when this one is opened
    setShowDoctorDropdown(false);
    setShowConditionDropdown(false);
    setShowMedicationDropdown(false);
    // Show dropdown when user types, regardless of results
    if (e.target.value.length >= 2) {
      setShowHospitalDropdown(true);
    } else {
      setShowHospitalDropdown(false);
    }
  };

  const handleDoctorSearchChange = (e) => {
    setDoctorSearch(e.target.value);
    // Close other dropdowns when this one is opened
    setShowHospitalDropdown(false);
    setShowConditionDropdown(false);
    setShowMedicationDropdown(false);
    // Show dropdown when user types, regardless of results
    if (e.target.value.length >= 2) {
      setShowDoctorDropdown(true);
    } else {
      setShowDoctorDropdown(false);
    }
  };

  const handleHospitalSelect = (hospital) => {
    // Parse address to extract city and ZIP code
    const addressParts = hospital.address.split(", ");
    let city = "",
      zipCode = "";

    // Look for ZIP code pattern (5 digits) and the city before it
    for (let i = addressParts.length - 1; i >= 0; i--) {
      const part = addressParts[i].trim();
      if (/^\d{5}(-\d{4})?$/.test(part)) {
        zipCode = part;
        if (i > 0) {
          // Get city and state (e.g., "WHITTIER, CA")
          city = addressParts[i - 1].trim();
        }
        break;
      }
    }

    // Set the temporary fields for manual entry
    setTempHospital(hospital.name);
    setForm((prev) => ({
      ...prev,
      tempHospitalCity: city,
      tempHospitalZip: zipCode,
    }));

    setHospitalSearch("");
    setShowHospitalDropdown(false);
  };

  const handleDoctorSelect = (doctor) => {
    setForm((prev) => ({
      ...prev,
      doctors: [
        ...prev.doctors,
        {
          name: doctor.name,
          status: tempDoctorStatus,
          specialization: doctor.specialty || tempDoctorSpecialization,
        },
      ],
    }));
    setDoctorSearch("");
    setShowDoctorDropdown(false);
  };

  const handleConditionSearchChange = (e) => {
    setConditionSearch(e.target.value);
    // Close other dropdowns when this one is opened
    setShowHospitalDropdown(false);
    setShowDoctorDropdown(false);
    setShowMedicationDropdown(false);
    if (e.target.value.length >= 2) {
      setShowConditionDropdown(true);
    } else {
      setShowConditionDropdown(false);
    }
  };

  const handleMedicationSearchChange = (e) => {
    setMedicationSearch(e.target.value);
    // Close other dropdowns when this one is opened
    setShowHospitalDropdown(false);
    setShowDoctorDropdown(false);
    setShowConditionDropdown(false);
    if (e.target.value.length >= 2) {
      setShowMedicationDropdown(true);
    } else {
      setShowMedicationDropdown(false);
    }
  };

  const handleConditionSelect = (condition) => {
    const conditionName = condition.consumerName || condition.primaryName;
    setForm((prev) => ({
      ...prev,
      conditions: [...prev.conditions, conditionName],
    }));
    setConditionSearch("");
    setShowConditionDropdown(false);
  };

  const handleMedicationSelect = (medication) => {
    setForm((prev) => ({
      ...prev,
      medications: [...prev.medications, medication.displayName],
    }));
    setMedicationSearch("");
    setShowMedicationDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({
      primaryPhone: "",
      bloodType: "",
      dob: "",
      ssn: "",
      allergies: "",
      medications: "",
      conditions: "",
      hospitals: "",
      doctors: "",
      secondaryPhone: "",
      secondaryMobile: "",
      religion: "",
      ekg: "",
      insuranceCard: "",
    });

    const workingForm = { ...form };

    let hasErrors = false;
    const newErrors = {
      primaryPhone: "",
      bloodType: "",
      dob: "",
      ssn: "",
      allergies: "",
      medications: "",
      conditions: "",
      hospitals: "",
      doctors: "",
      secondaryPhone: "",
      secondaryMobile: "",
      religion: "",
      ekg: "",
      insuranceCard: "",
    };

    // Validate date format (only if not default date)
    if (workingForm.dob && workingForm.dob.trim() !== "01/01/1900") {
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(workingForm.dob)) {
        newErrors.dob = t("invalidDateFormat", lang);
        hasErrors = true;
      } else {
        // Check if date is valid (not in future and reasonable past)
        const inputDate = new Date(workingForm.dob);
        const today = new Date();
        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 120); // 120 years ago

        if (inputDate > today) {
          newErrors.dob = t("dateInFuture", lang);
          hasErrors = true;
        } else if (inputDate < minDate) {
          newErrors.dob = t("dateInvalid", lang);
          hasErrors = true;
        }
      }
    }

    // Validate SSN format (only if not default)
    if (workingForm.ssn && workingForm.ssn.trim() !== "000-00-0000") {
      const ssnRegex = /^\d{3}-\d{2}-\d{4}$/;
      if (!ssnRegex.test(workingForm.ssn)) {
        newErrors.ssn = t("invalidSSNFormat", lang);
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Get token from localStorage (you might need to adjust this based on how you store the token)
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Prepare the card data according to the API structure using workingForm
      const cardData = {
        allergies: workingForm.allergies.map((allergy) => ({ name: allergy })),
        medications: workingForm.medications.map((medication) => ({
          name: medication,
        })),
        conditions: workingForm.conditions.map((condition) => ({
          name: condition,
        })),
        hospitals: workingForm.hospitals.map((hospital) => ({
          name: hospital.name,
          city: hospital.city || "",
          zipCode: hospital.zipCode || "",
        })),
        doctors: workingForm.doctors.map((doctor) => ({
          name: doctor.name,
          status: doctor.status,
          specialization: doctor.specialization || "General Practice",
        })),
        primaryPhoneNumber:
          workingForm.primaryPhone || workingForm.primaryPhoneNumber,
        secondaryPhoneNumber:
          workingForm.secondaryPhone || workingForm.secondaryPhoneNumber,
        secondaryMobileNumber: "",
        primaryInsurance:
          workingForm.primaryInsurance.length > 0
            ? workingForm.primaryInsurance[0]
            : "",
        primaryInsuranceMemberId: workingForm.primaryInsuranceMemberId || "",
        secondaryInsurance:
          workingForm.secondaryInsurance.length > 0
            ? workingForm.secondaryInsurance[0]
            : "",
        secondaryInsuranceMemberId:
          workingForm.secondaryInsuranceMemberId || "",
        religion: workingForm.religion || "",
        ethnicity: "not_specified", // Hardcoded value for API
        organDonor: workingForm.organDonor ? true : false,
        isSmoker: workingForm.isSmoker ? true : false,
        bloodType:
          workingForm.bloodType === "other"
            ? workingForm.otherBloodType
            : workingForm.bloodType || "",
        dateOfBirth: workingForm.dob
          ? (() => {
              const parts = workingForm.dob.split("/");
              if (parts.length === 3) {
                return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
              }
              return workingForm.dob;
            })()
          : "",
        ssn: workingForm.ssn || "",
        ekg: typeof workingForm.ekg === "string" ? workingForm.ekg : "",
        insuranceCard:
          typeof workingForm.insuranceCard === "string"
            ? workingForm.insuranceCard
            : "",
      };

      // Call the create card API
      const response = await createPatientCard(cardData, token);

      // Generate QR code after successful card creation
      try {
        const qrResponse = await generateQRCode(token);

        // Call the original onSubmit with the workingForm data, card response, and QR response
        onSubmit(workingForm, response, qrResponse);
      } catch (qrError) {
        console.error("Failed to generate QR code:", qrError);
        // Still proceed with card creation even if QR generation fails
        onSubmit(workingForm, response, null);
      }
    } catch (error) {
      console.error("Failed to create card:", error);

      // Initialize error object
      const newErrors = {
        primaryPhone: "",
        bloodType: "",
        dob: "",
        ssn: "",
        allergies: "",
        medications: "",
        conditions: "",
        hospitals: "",
        doctors: "",
        secondaryPhone: "",
        religion: "",
        ekg: "",
      };

      // Parse error message - handleApiResponse throws errors with message format:
      // "Validation error: error1, error2, ..." or just a plain message
      const errorMessage = error.message || "";

      if (errorMessage.startsWith("Validation error:")) {
        // Extract individual error messages from "Validation error: error1, error2, ..."
        const errorsString = errorMessage
          .replace("Validation error:", "")
          .trim();
        const errorMessages = errorsString.split(",").map((err) => err.trim());

        errorMessages.forEach((errorMsg) => {
          const errorLower = errorMsg.toLowerCase();
          if (
            errorLower.includes("primaryphonenumber") ||
            errorLower.includes("primary phone") ||
            (errorLower.includes("phone") && errorLower.includes("primary"))
          ) {
            newErrors.primaryPhone = errorMsg;
          } else if (
            errorLower.includes("secondaryphonenumber") ||
            errorLower.includes("secondary phone") ||
            (errorLower.includes("phone") && errorLower.includes("secondary"))
          ) {
            newErrors.secondaryPhone = errorMsg;
          } else if (
            errorLower.includes("bloodtype") ||
            errorLower.includes("blood type")
          ) {
            newErrors.bloodType = errorMsg;
          } else if (
            errorLower.includes("dateofbirth") ||
            errorLower.includes("date of birth") ||
            errorLower.includes("dob")
          ) {
            newErrors.dob = errorMsg;
          } else if (errorLower.includes("ssn")) {
            newErrors.ssn = errorMsg;
          } else if (errorLower.includes("allerg")) {
            newErrors.allergies = errorMsg;
          } else if (errorLower.includes("medication")) {
            newErrors.medications = errorMsg;
          } else if (errorLower.includes("condition")) {
            newErrors.conditions = errorMsg;
          } else if (errorLower.includes("hospital")) {
            newErrors.hospitals = errorMsg;
          } else if (errorLower.includes("doctor")) {
            newErrors.doctors = errorMsg;
          } else if (errorLower.includes("religion")) {
            newErrors.religion = errorMsg;
          } else if (errorLower.includes("ekg")) {
            newErrors.ekg = errorMsg;
          } else if (errorLower.includes("phone")) {
            // Generic phone error - show on primary phone field
            newErrors.primaryPhone = errorMsg;
          }
        });
      } else if (
        errorMessage.toLowerCase().includes("phone") ||
        errorMessage.toLowerCase().includes("validation")
      ) {
        // Single error message about phone or validation
        newErrors.primaryPhone = errorMessage;
      } else if (errorMessage) {
        // Other error messages - show on primary phone field
        newErrors.primaryPhone = errorMessage;
      } else {
        // Fallback
        newErrors.primaryPhone = "Failed to create card. Please try again.";
      }

      setErrors(newErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Insurance provider options
  const secondaryInsuranceProviders = [
    { value: "BCBS", label: "Blue Cross Blue Shield" },
    { value: "UnitedHealthcare", label: "UnitedHealthcare" },
    { value: "Elevance", label: "Elevance Health / Anthem" },
    { value: "Centene", label: "Centene Corporation" },
    { value: "Humana", label: "Humana Inc." },
    { value: "CVS", label: "CVS Health / Aetna" },
    { value: "Kaiser", label: "Kaiser Permanente" },
    { value: "Cigna", label: "Cigna Group" },
    { value: "HCSC", label: "Health Care Service Corporation (HCSC)" },
    { value: "Molina", label: "Molina Healthcare Inc." },
    { value: "GuideWell", label: "GuideWell / Florida Blue" },
    { value: "Independence", label: "Independence Health Group" },
    { value: "Highmark", label: "Highmark Health" },
    { value: "UPMC", label: "UPMC Health Plan" },
    {
      value: "Point32Health",
      label: "Point32Health (Harvard Pilgrim + Tufts)",
    },
    { value: "Priority", label: "Priority Health" },
    { value: "MVP", label: "MVP Health Care" },
    { value: "CDPHP", label: "CDPHP" },
    { value: "Healthfirst", label: "Healthfirst" },
    { value: "MetroPlus", label: "MetroPlusHealth" },
    { value: "Geisinger", label: "Geisinger Health Plan" },
    { value: "Medica", label: "Medica Inc." },
    { value: "Sanford", label: "Sanford Health Plan" },
    { value: "Quartz", label: "Quartz Health Solutions" },
    { value: "Dean", label: "Dean Health Plan" },
    { value: "Oscar", label: "Oscar Health" },
    { value: "Bright", label: "Bright Health" },
    { value: "Wellpoint", label: "Wellpoint / Elevance" },
    { value: "Amerigroup", label: "Amerigroup Corporation" },
    { value: "CareSource", label: "CareSource" },
    { value: "HealthNet", label: "Health Net Incorporated" },
    { value: "Aetna", label: "Aetna Inc." },
    { value: "Beacon", label: "Beacon Health Options" },
    {
      value: "MedicaidAdvantage",
      label: "Medicaid Advantage Specialist Plans",
    },
    {
      value: "MedicareAdvantage",
      label: "Medicare Advantage Specialist Plans",
    },
    { value: "Centivo", label: "Centivo" },
    { value: "Alignment", label: "Alignment Healthcare" },
    { value: "Aloha", label: "Aloha Care" },
    { value: "AmeriHealth", label: "AmeriHealth Carriers" },
    { value: "other", label: t("other", lang) || "Other" },
  ];

  // Insurance provider options
  const insuranceProviders = [
    { value: "", label: t("selectInsurance", lang) || "Select Insurance" },
    { value: "MedicareA", label: "Medicare Part A – Hospital" },
    { value: "MedicareB", label: "Medicare Part B – Medical Insurance" },
    { value: "MedicareC", label: "Medicare Part C – Medicare Advantage" },
    {
      value: "MedicareD",
      label: "Medicare Part D – Additional Coverage OR Secondary Coverage",
    },

    {
      value: "MedicareA_B",
      label: "Medicare Part A – Hospital + Medicare Part B – Medical Insurance",
    },
    {
      value: "MedicareA_C",
      label:
        "Medicare Part A – Hospital + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareA_D",
      label:
        "Medicare Part A – Hospital + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareB_C",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareB_D",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareC_D",
      label:
        "Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },

    {
      value: "MedicareA_B_C",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareA_B_D",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareA_C_D",
      label:
        "Medicare Part A – Hospital + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareB_C_D",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareA_B_C_D",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    { value: "BCBS", label: "Blue Cross Blue Shield" },
    { value: "UnitedHealthcare", label: "UnitedHealthcare" },
    { value: "Elevance", label: "Elevance Health / Anthem" },
    { value: "Centene", label: "Centene Corporation" },
    { value: "Humana", label: "Humana Inc." },
    { value: "CVS", label: "CVS Health / Aetna" },
    { value: "Kaiser", label: "Kaiser Permanente" },
    { value: "Cigna", label: "Cigna Group" },
    { value: "HCSC", label: "Health Care Service Corporation (HCSC)" },
    { value: "Molina", label: "Molina Healthcare Inc." },
    { value: "GuideWell", label: "GuideWell / Florida Blue" },
    { value: "Independence", label: "Independence Health Group" },
    { value: "Highmark", label: "Highmark Health" },
    { value: "UPMC", label: "UPMC Health Plan" },
    {
      value: "Point32Health",
      label: "Point32Health (Harvard Pilgrim + Tufts)",
    },
    { value: "Priority", label: "Priority Health" },
    { value: "MVP", label: "MVP Health Care" },
    { value: "CDPHP", label: "CDPHP" },
    { value: "Healthfirst", label: "Healthfirst" },
    { value: "MetroPlus", label: "MetroPlusHealth" },
    { value: "Geisinger", label: "Geisinger Health Plan" },
    { value: "Medica", label: "Medica Inc." },
    { value: "Sanford", label: "Sanford Health Plan" },
    { value: "Quartz", label: "Quartz Health Solutions" },
    { value: "Dean", label: "Dean Health Plan" },
    { value: "Oscar", label: "Oscar Health" },
    { value: "Bright", label: "Bright Health" },
    { value: "Wellpoint", label: "Wellpoint / Elevance" },
    { value: "Amerigroup", label: "Amerigroup Corporation" },
    { value: "CareSource", label: "CareSource" },
    { value: "HealthNet", label: "Health Net Incorporated" },
    { value: "Aetna", label: "Aetna Inc." },
    { value: "Beacon", label: "Beacon Health Options" },
    {
      value: "MedicaidAdvantage",
      label: "Medicaid Advantage Specialist Plans",
    },
    {
      value: "MedicareAdvantage",
      label: "Medicare Advantage Specialist Plans",
    },
    { value: "Centivo", label: "Centivo" },
    { value: "Alignment", label: "Alignment Healthcare" },
    { value: "Aloha", label: "Aloha Care" },
    { value: "AmeriHealth", label: "AmeriHealth Carriers" },
    { value: "other", label: t("other", lang) || "Other" },
  ];

  // Religion options
  const religionOptions = [
    { value: "", label: t("choose", lang) },
    { value: "catholic", label: t("catholic", lang) },
    { value: "protestant", label: t("protestant", lang) },
    { value: "orthodox", label: t("orthodox", lang) },
    { value: "baptist", label: t("baptist", lang) },
    { value: "methodist", label: t("methodist", lang) },
    { value: "lutheran", label: t("lutheran", lang) },
    { value: "presbyterian", label: t("presbyterian", lang) },
    { value: "episcopal", label: t("episcopal", lang) },
    { value: "christianity", label: t("christianity", lang) },
    { value: "islam", label: t("islam", lang) },
    { value: "hinduism", label: t("hinduism", lang) },
    { value: "buddhism", label: t("buddhism", lang) },
    { value: "judaism", label: t("judaism", lang) },
    { value: "sikhism", label: t("sikhism", lang) },
    { value: "other", label: t("other", lang) },
  ];

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">
        {t("searching", lang)}
      </span>
    </div>
  );

  // No results component
  const NoResultsMessage = ({ type, searchTerm }) => {
    let messageKey;
    switch (type) {
      case "hospitals":
        messageKey = "noHospitalsFound";
        break;
      case "doctors":
        messageKey = "noDoctorsFound";
        break;
      case "conditions":
        messageKey = "noConditionsFound";
        break;
      case "medications":
        messageKey = "noMedicationsFound";
        break;
      default:
        messageKey = "noResultsFound";
    }
    return (
      <div className="p-3 text-center">
        <div className="text-xs text-[#837f90] mb-1">
          {t(messageKey, lang).replace("{searchTerm}", searchTerm)}
        </div>
        <div className="text-xs text-[#837f90] opacity-75">
          {t("tryDifferentSearch", lang)}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-11/12 sm:max-w-lg sm:w-full border border-[#e5e7eb] relative max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="text-xl font-extrabold text-[#2d348f] mb-4 text-center">
          {t("createYourCard", lang)}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {/* Allergies Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("allergies", lang)}
            </label>

            <div className="flex gap-2">
              <input
                value={tempAllergy}
                onChange={(e) => {
                  // if (!isNoAllergies) {
                  //   setTempAllergy(e.target.value);
                  // }
                  setTempAllergy(e.target.value);
                }}
                placeholder={t("addAllergy", lang) || "Add allergy..."}
                // disabled={isNoAllergies}
                // className={`flex-1 border rounded p-2 text-sm ${
                //   errors.allergies ? "border-red-500" : ""
                // } ${isNoAllergies ? "bg-gray-100 cursor-not-allowed" : ""}`}
                className={`flex-1 border rounded p-2 text-sm ${
                  errors.allergies ? "border-red-500" : ""
                } `}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addAllergy())
                }
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addAllergy();
                }}
                // disabled={isNoAllergies}
                className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            {/* None option checkbox */}
            {/* <div className="mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isNoAllergies}
                  onChange={handleNoAllergiesChange}
                  className="w-4 h-4 text-[#4962d8] border-gray-300 rounded focus:ring-[#4962d8] focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-700">None</span>
              </label>
            </div> */}
            {errors.allergies && (
              <div className="text-red-500 text-xs">*{errors.allergies}</div>
            )}
            {form.allergies.length > 0 && (
              <div className="space-y-1">
                {form.allergies.map((allergy, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#f7f8fa] p-2 rounded text-sm"
                  >
                    <span>{allergy}</span>
                    <button
                      type="button"
                      onClick={() => removeAllergy(index)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medications field with search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("medications", lang)}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                value={tempMedication}
                onChange={(e) => setTempMedication(e.target.value)}
                placeholder={
                  t("addMedication", lang) || "Add medication manually..."
                }
                className={`flex-1 border rounded p-2 text-sm ${
                  errors.medications ? "border-red-500" : ""
                }`}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addMedication())
                }
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addMedication();
                }}
                className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors"
              >
                +
              </button>
            </div>
            <div className="relative" ref={medicationDropdownRef}>
              <div className="flex items-center gap-2">
                <input
                  value={medicationSearch}
                  onChange={handleMedicationSearchChange}
                  placeholder={
                    t("searchMedications", lang) || "Or search medications..."
                  }
                  className="flex-1 border rounded p-2 text-sm"
                />
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                  {isMedicationLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#837f90"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  )}
                </span>
              </div>
              {showMedicationDropdown && (
                <div className="absolute left-0 top-10 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-32 overflow-y-auto">
                  {isMedicationLoading ? (
                    <LoadingSpinner />
                  ) : medicationResults.length > 0 ? (
                    medicationResults.map((medication, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0 text-sm"
                        onClick={() => handleMedicationSelect(medication)}
                      >
                        <div className="font-semibold text-[#2d348f]">
                          {medication.displayName}
                        </div>
                        {medication.strengthsAndForms.length > 0 && (
                          <div className="text-xs text-[#837f90]">
                            Available:{" "}
                            {medication.strengthsAndForms
                              .slice(0, 2)
                              .join(", ")}
                            {medication.strengthsAndForms.length > 2 && "..."}
                          </div>
                        )}
                      </button>
                    ))
                  ) : medicationSearch.length >= 2 ? (
                    <NoResultsMessage
                      type="medications"
                      searchTerm={medicationSearch}
                    />
                  ) : null}
                </div>
              )}
            </div>
            {errors.medications && (
              <div className="text-red-500 text-xs">*{errors.medications}</div>
            )}
            {form.medications.length > 0 && (
              <div className="space-y-1">
                {form.medications.map((medication, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#f7f8fa] p-2 rounded text-sm"
                  >
                    <span>{medication}</span>
                    <button
                      type="button"
                      onClick={() => removeMedication(index)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conditions field with search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("conditions", lang)}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                value={tempCondition}
                onChange={(e) => setTempCondition(e.target.value)}
                placeholder={
                  t("addCondition", lang) || "Add condition manually..."
                }
                className={`flex-1 border rounded p-2 text-sm ${
                  errors.conditions ? "border-red-500" : ""
                }`}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addCondition())
                }
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addCondition();
                }}
                className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors"
              >
                +
              </button>
            </div>
            <div className="relative" ref={conditionDropdownRef}>
              <div className="flex items-center gap-2">
                <input
                  value={conditionSearch}
                  onChange={handleConditionSearchChange}
                  placeholder={
                    t("searchConditions", lang) || "Or search conditions..."
                  }
                  className="flex-1 border rounded p-2 text-sm"
                />
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                  {isConditionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#837f90"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  )}
                </span>
              </div>
              {showConditionDropdown && (
                <div className="absolute left-0 top-10 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-32 overflow-y-auto">
                  {isConditionLoading ? (
                    <LoadingSpinner />
                  ) : conditionResults.length > 0 ? (
                    conditionResults.map((condition, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0 text-sm"
                        onClick={() => handleConditionSelect(condition)}
                      >
                        <div className="font-semibold text-[#2d348f]">
                          {condition.consumerName || condition.primaryName}
                        </div>
                        <div className="text-xs text-[#837f90]">
                          {condition.icd9Code && `ICD-9: ${condition.icd9Code}`}
                          {condition.icd10cmCodes &&
                            condition.icd9Code &&
                            " • "}
                          {condition.icd10cmCodes &&
                            `ICD-10: ${condition.icd10cmCodes}`}
                        </div>
                      </button>
                    ))
                  ) : conditionSearch.length >= 2 ? (
                    <NoResultsMessage
                      type="conditions"
                      searchTerm={conditionSearch}
                    />
                  ) : null}
                </div>
              )}
            </div>
            {errors.conditions && (
              <div className="text-red-500 text-xs">*{errors.conditions}</div>
            )}
            {form.conditions.length > 0 && (
              <div className="space-y-1">
                {form.conditions.map((condition, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#f7f8fa] p-2 rounded text-sm"
                  >
                    <span>{condition}</span>
                    <button
                      type="button"
                      onClick={() => removeCondition(index)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hospital field with search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("hospitals", lang) || "Hospitals"}
            </label>
            <div className="space-y-4 mb-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#837f90]">
                  {t("addHospital", lang) || "Add hospital name"}
                </label>
                <input
                  value={tempHospital}
                  onChange={(e) => setTempHospital(e.target.value)}
                  placeholder={t("addHospital", lang) || "Add hospital name..."}
                  className={`w-full border rounded p-2 text-sm ${
                    errors.hospitals ? "border-red-500" : ""
                  }`}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-semibold text-[#837f90]">
                    {t("hospitalCity", lang) || "Hospital City"}
                  </label>
                  <input
                    type="text"
                    value={form.tempHospitalCity || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        tempHospitalCity: e.target.value,
                      }))
                    }
                    placeholder={
                      t("enterHospitalCity", lang) || "Enter hospital city..."
                    }
                    className="w-full border rounded p-2 text-sm"
                  />
                </div>
                <div className="w-32 space-y-1">
                  <label className="text-xs font-semibold text-[#837f90]">
                    {t("hospitalZipCode", lang) || "ZIP Code"}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.tempHospitalZip || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          tempHospitalZip: e.target.value,
                        }))
                      }
                      placeholder={
                        t("enterHospitalZipCode", lang) || "Enter ZIP code..."
                      }
                      className="w-full border rounded p-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        addHospital();
                      }}
                      className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative" ref={hospitalDropdownRef}>
              <div className="flex items-center gap-2">
                <input
                  value={hospitalSearch}
                  onChange={handleHospitalSearchChange}
                  placeholder={
                    t("searchHospitals", lang) || "Or search hospitals..."
                  }
                  className="flex-1 border rounded p-2 text-sm"
                />
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                  {isHospitalLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#837f90"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  )}
                </span>
              </div>
              {showHospitalDropdown && (
                <div className="absolute left-0 top-10 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-32 overflow-y-auto">
                  {isHospitalLoading ? (
                    <LoadingSpinner />
                  ) : hospitalResults.length > 0 ? (
                    hospitalResults.map((hosp, idx) => (
                      <button
                        key={hosp.npi + idx}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0 text-sm"
                        onClick={() => handleHospitalSelect(hosp)}
                      >
                        <div className="font-semibold text-[#2d348f]">
                          {hosp.name}
                        </div>
                        <div className="text-xs text-[#837f90]">
                          {hosp.address.split(", ").map((part, idx, arr) => {
                            // Check if this part is a ZIP code
                            if (/^\d{5}(-\d{4})?$/.test(part.trim())) {
                              // If it's a ZIP code, combine it with the city (previous part)
                              if (idx > 0) {
                                return (
                                  <span key={idx} className="font-medium">
                                    {arr[idx - 1]}, {part}
                                  </span>
                                );
                              }
                              return (
                                <span key={idx} className="font-medium">
                                  {part}
                                </span>
                              );
                            }
                            // Don't render city here as it's handled with ZIP code
                            if (
                              idx < arr.length - 1 &&
                              /^\d{5}(-\d{4})?$/.test(arr[idx + 1].trim())
                            ) {
                              return null;
                            }
                            // Render other address parts
                            return (
                              <span key={idx}>
                                {part}
                                {idx < arr.length - 1 ? ", " : ""}
                              </span>
                            );
                          })}
                        </div>
                      </button>
                    ))
                  ) : hospitalSearch.length >= 2 ? (
                    <NoResultsMessage
                      type="hospitals"
                      searchTerm={hospitalSearch}
                    />
                  ) : null}
                </div>
              )}
            </div>
            {errors.hospitals && (
              <div className="text-red-500 text-xs">*{errors.hospitals}</div>
            )}
            {form.hospitals.length > 0 && (
              <div className="space-y-1">
                {form.hospitals.map((hospital, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#f7f8fa] p-2 rounded text-sm"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{hospital.name}</span>
                      {(hospital.city || hospital.zipCode) && (
                        <span className="text-xs text-gray-600">
                          {[hospital.city, hospital.zipCode]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeHospital(index)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Doctor field with search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("doctors", lang) || "Doctors"}
            </label>
            <div className="flex gap-2 mb-2 overflow-x-auto">
              <input
                value={tempDoctor}
                onChange={(e) => setTempDoctor(e.target.value)}
                placeholder={t("addDoctor", lang) || "Add doctor manually..."}
                className={`flex-1 border rounded p-2 text-sm ${
                  errors.doctors ? "border-red-500" : ""
                }`}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addDoctor())
                }
              />
              <select
                value={tempDoctorStatus}
                onChange={(e) => setTempDoctorStatus(e.target.value)}
                className="border rounded p-2 text-sm bg-white"
              >
                <option value="current">Main</option>
                <option value="past">
                  Doctor{" "}
                  {form.doctors.filter((d) => d.status === "past").length + 1}
                </option>
              </select>
              <select
                value={tempDoctorSpecialization}
                onChange={(e) => setTempDoctorSpecialization(e.target.value)}
                className="border rounded p-2 text-sm bg-white"
              >
                <option value="General Practice">General Practice</option>
                <option value="Cardiology">Cardiology</option>
                <option value="Dermatology">Dermatology</option>
                <option value="Endocrinology">Endocrinology</option>
                <option value="Neurology">Neurology</option>
                <option value="Orthopedics">Orthopedics</option>
                <option value="Pediatrics">Pediatrics</option>
                <option value="Psychiatry">Psychiatry</option>
                <option value="Radiology">Radiology</option>
                <option value="Surgery">Surgery</option>
              </select>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addDoctor();
                }}
                className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors"
              >
                +
              </button>
            </div>
            <div className="relative" ref={doctorDropdownRef}>
              <div className="flex items-center gap-2 overflow-x-auto">
                <input
                  value={doctorSearch}
                  onChange={handleDoctorSearchChange}
                  placeholder={
                    t("searchDoctors", lang) || "Or search doctors..."
                  }
                  className="flex-1 border rounded p-2 text-sm"
                />
                <select
                  value={tempDoctorStatus}
                  onChange={(e) => setTempDoctorStatus(e.target.value)}
                  className="border rounded p-2 text-sm bg-white"
                >
                  <option value="current">Main</option>
                  <option value="past">
                    Doctor{" "}
                    {form.doctors.filter((d) => d.status === "past").length + 1}
                  </option>
                </select>
                <select
                  value={tempDoctorSpecialization}
                  onChange={(e) => setTempDoctorSpecialization(e.target.value)}
                  className="border rounded p-2 text-sm bg-white"
                >
                  <option value="General Practice">General Practice</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="Endocrinology">Endocrinology</option>
                  <option value="Neurology">Neurology</option>
                  <option value="Orthopedics">Orthopedics</option>
                  <option value="Pediatrics">Pediatrics</option>
                  <option value="Psychiatry">Psychiatry</option>
                  <option value="Radiology">Radiology</option>
                  <option value="Surgery">Surgery</option>
                </select>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                  {isDoctorLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#837f90"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                  )}
                </span>
              </div>
              {showDoctorDropdown && (
                <div className="absolute left-0 top-10 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-32 overflow-y-auto">
                  {isDoctorLoading ? (
                    <LoadingSpinner />
                  ) : doctorResults.length > 0 ? (
                    doctorResults.map((doc, idx) => (
                      <button
                        key={doc.npi + idx}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0 text-sm"
                        onClick={() => handleDoctorSelect(doc)}
                      >
                        <div className="font-semibold text-[#2d348f]">
                          {doc.name}
                        </div>
                        <div className="text-xs text-[#837f90]">
                          {doc.specialty}
                        </div>
                      </button>
                    ))
                  ) : doctorSearch.length >= 2 ? (
                    <NoResultsMessage
                      type="doctors"
                      searchTerm={doctorSearch}
                    />
                  ) : null}
                </div>
              )}
            </div>
            {errors.doctors && (
              <div className="text-red-500 text-xs">*{errors.doctors}</div>
            )}
            {form.doctors.length > 0 && (
              <div className="space-y-2">
                {/* Main Doctor */}
                {form.doctors.filter((doctor) => doctor.status === "current")
                  .length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-[#2d348f] mb-1">
                      Main Doctor:
                    </div>
                    <div className="space-y-1">
                      {form.doctors
                        .filter((doctor) => doctor.status === "current")
                        .map((doctor, index) => {
                          const originalIndex = form.doctors.findIndex(
                            (d) => d === doctor,
                          );
                          return (
                            <div
                              key={originalIndex}
                              className="flex items-center justify-between bg-green-50 border border-green-200 p-2 rounded text-sm"
                            >
                              <span className="font-medium">{doctor.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  Main
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleDoctorStatus(originalIndex)
                                  }
                                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                                  title="Make as Doctor"
                                >
                                  Make as Doctor{" "}
                                  {form.doctors.filter(
                                    (d) => d.status === "past",
                                  ).length + 1}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeDoctor(originalIndex)}
                                  className="text-red-500 hover:text-red-700 text-lg font-bold"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Other Doctors */}
                {form.doctors.filter((doctor) => doctor.status === "past")
                  .length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-[#2d348f] mb-1">
                      Other Doctors:
                    </div>
                    <div className="space-y-1">
                      {form.doctors
                        .filter((doctor) => doctor.status === "past")
                        .map((doctor, index) => {
                          const originalIndex = form.doctors.findIndex(
                            (d) => d === doctor,
                          );
                          return (
                            <div
                              key={originalIndex}
                              className="flex items-center justify-between bg-gray-50 border border-gray-200 p-2 rounded text-sm"
                            >
                              <span className="font-medium">{doctor.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                  Doctor {index + 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleDoctorStatus(originalIndex)
                                  }
                                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                                  title="Make as Main"
                                >
                                  Make as Main
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeDoctor(originalIndex)}
                                  className="text-red-500 hover:text-red-700 text-lg font-bold"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <label className="text-sm font-medium text-[#2d348f]">
            Doctor's Contact
          </label>
          {/* <input
            name="contact"
            value={form.contact}
            onChange={handlePhoneChange}
            placeholder="Enter Doctor's contact"
            className="border rounded p-2 text-sm"
          /> */}

          <AccountInput
            name="contact"
            value={form.contact}
            onChange={handlePhoneChange}
            placeholder="Enter Doctor's contact"
            className="border rounded p-2 text-sm"
            isPhoneInput
          />

          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("primaryPhone", lang)}
            </label>
            {/* <input
              name="primaryPhone"
              value={form.primaryPhone}
              onChange={handlePhoneChange}
              placeholder={
                t("primaryPhone", lang) || "XXX-XXX-XXXX or +XX-XXX-XXXXXXX"
              }
              className={`border rounded p-2 text-sm w-full ${
                errors.primaryPhone ? "border-red-500" : ""
              }`}
            /> */}
            <AccountInput
              value={form.primaryPhone}
              onChange={handlePhoneChange}
              name="primaryPhone"
              isPhoneInput={true}
              placeholder={
                t("primaryPhone", lang) || "XXX-XXX-XXXX or +XX-XXX-XXXXXXX"
              }
              className={`border rounded p-2 text-sm w-full ${
                errors.primaryPhone ? "border-red-500" : ""
              }`}
            />
            {errors.primaryPhone && (
              <div className="text-red-500 text-xs">*{errors.primaryPhone}</div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("secondaryPhone", lang)}
            </label>
            {/* <input
              name="secondaryPhone"
              value={form.secondaryPhone}
              onChange={handlePhoneChange}
              placeholder={
                t("secondaryPhone", lang) || "XXX-XXX-XXXX or +XX-XXX-XXXXXXX"
              }
              className={`border rounded p-2 text-sm w-full ${
                errors.secondaryPhone ? "border-red-500" : ""
              }`}
            /> */}
            <AccountInput
              name="secondaryPhone"
              value={form.secondaryPhone}
              onChange={handlePhoneChange}
              placeholder={
                t("secondaryPhone", lang) || "XXX-XXX-XXXX or +XX-XXX-XXXXXXX"
              }
              className={`border rounded p-2 text-sm w-full ${
                errors.secondaryPhone ? "border-red-500" : ""
              }`}
              isPhoneInput
            />
            {errors.secondaryPhone && (
              <div className="text-red-500 text-xs">
                *{errors.secondaryPhone}
              </div>
            )}
          </div>

          {/* Primary Insurance Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("primaryInsurance", lang)}
            </label>
            <select
              value={selectedPrimaryInsurance}
              onChange={handlePrimaryInsuranceChange}
              className="w-full border rounded p-2 text-sm bg-white"
            >
              {insuranceProviders.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedPrimaryInsurance === "other" && (
              <input
                type="text"
                value={otherPrimaryInsurance}
                onChange={handleOtherPrimaryInsurance}
                placeholder={
                  t("enterOtherInsurance", lang) || "Enter other insurance"
                }
                className="w-full border rounded p-2 text-sm mt-2"
              />
            )}
            {form.primaryInsurance.length > 0 && (
              <div className="space-y-1 mt-2">
                {form.primaryInsurance.map((insurance, index) => (
                  <div key={index} className="bg-[#f7f8fa] p-2 rounded text-sm">
                    <span>{insurance}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Primary Insurance Member ID */}
            <div className="mt-2">
              <label className="text-sm font-medium text-[#2d348f]">
                {t("primaryInsuranceMemberId", lang)}
              </label>
              <input
                type="text"
                value={form.primaryInsuranceMemberId || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryInsuranceMemberId: e.target.value,
                  }))
                }
                placeholder={t("enterPrimaryMemberId", lang)}
                className="w-full border rounded p-2 text-sm mt-1"
              />
            </div>
          </div>

          {/* Secondary Insurance Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("secondaryInsurance", lang)}
            </label>
            <select
              value={selectedSecondaryInsurance}
              onChange={handleSecondaryInsuranceChange}
              className="w-full border rounded p-2 text-sm bg-white"
            >
              {secondaryInsuranceProviders.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedSecondaryInsurance === "other" && (
              <input
                type="text"
                value={otherSecondaryInsurance}
                onChange={handleOtherSecondaryInsurance}
                placeholder={
                  t("enterOtherInsurance", lang) || "Enter other insurance"
                }
                className="w-full border rounded p-2 text-sm mt-2"
              />
            )}
            {form.secondaryInsurance.length > 0 && (
              <div className="space-y-1 mt-2">
                {form.secondaryInsurance.map((insurance, index) => (
                  <div key={index} className="bg-[#f7f8fa] p-2 rounded text-sm">
                    <span>{insurance}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Secondary Insurance Member ID */}
            <div className="mt-2">
              <label className="text-sm font-medium text-[#2d348f]">
                {t("secondaryInsuranceMemberId", lang)}
              </label>
              <input
                type="text"
                value={form.secondaryInsuranceMemberId || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    secondaryInsuranceMemberId: e.target.value,
                  }))
                }
                placeholder={t("enterSecondaryMemberId", lang)}
                className="w-full border rounded p-2 text-sm mt-1"
              />
            </div>
          </div>

          {/* Upload Insurance Card Section */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {" "}
              Upload Insurance Card
            </label>
            <div className="flex flex-col gap-2">
              <label
                className={`border rounded p-2 text-sm w-full cursor-pointer flex items-center justify-between ${
                  errors.insuranceCard ? "border-red-500" : "border-gray-300"
                } hover:border-[#4962d8] transition-colors`}
              >
                <span className="text-gray-600 truncate flex-1">
                  {insuranceCardFileName ||
                    "Upload Insurance Card" ||
                    "Choose insurance card file..."}
                </span>
                <span className="text-[#4962d8] font-medium ml-2 whitespace-nowrap">
                  {insuranceCardFileName ? "Change File" : "Browse"}
                </span>
                <input
                  type="file"
                  name="insuranceCard"
                  onChange={handleInsuranceCardFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                />
              </label>
              {insuranceCardFileName && (
                <div className="flex items-center justify-between bg-gray-50 rounded p-2 text-xs">
                  <span className="text-gray-700 truncate flex-1">
                    {insuranceCardFileName}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveInsuranceCardFile}
                    className="text-red-500 hover:text-red-700 ml-2 font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            {errors.insuranceCard && (
              <div className="text-red-500 text-xs">
                *{errors.insuranceCard}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Accepted formats: JPEG, PNG, GIF, WebP (Max 10MB)
            </p>
          </div>

          <label className="text-sm font-medium text-[#2d348f]">
            {t("religion", lang)}
          </label>
          <div className="space-y-1">
            <select
              name="religion"
              value={form.religion}
              onChange={handleChange}
              className={`border rounded p-2 text-sm w-full ${
                errors.religion ? "border-red-500" : ""
              }`}
            >
              {religionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.religion && (
              <div className="text-red-500 text-xs">*{errors.religion}</div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="organDonor"
              checked={form.organDonor}
              onChange={handleChange}
            />
            {t("organDonor", lang)}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="isSmoker"
              checked={form.isSmoker}
              onChange={handleChange}
            />
            {t("Smoker", lang)}
          </label>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("bloodType", lang)}
            </label>
            <select
              name="bloodType"
              value={form.bloodType}
              onChange={handleChange}
              className={`border rounded p-2 text-sm w-full ${
                errors.bloodType ? "border-red-500" : ""
              }`}
            >
              <option value="">{t("selectBloodType", lang)}</option>
              <option value="A+">A⁺</option>
              <option value="A-">A⁻</option>
              <option value="B+">B⁺</option>
              <option value="B-">B⁻</option>
              <option value="AB+">AB⁺</option>
              <option value="AB-">AB⁻</option>
              <option value="O+">O⁺</option>
              <option value="O-">O⁻</option>
              <option value="Bombay (hh)">Bombay (hh)</option>
              <option value="A2+">A2⁺</option>
              <option value="A2-">A2⁻</option>
              <option value="A3+">A3⁺</option>
              <option value="A3-">A3⁻</option>
              <option value="Ax+">Ax⁺</option>
              <option value="Ax-">Ax⁻</option>
              <option value="O weak+">O weak⁺</option>
              <option value="O weak-">O weak⁻</option>
              <option value="B3+">B3⁺</option>
              <option value="B3-">B3⁻</option>
              <option value="Rh-null">Rh-null</option>
              <option value="Rh-variant (D-weak)">Rh-variant (D-weak)</option>
              <option value="other">{t("other", lang)}</option>
            </select>

            {/* Manual input for "Other" blood type */}
            {errors.bloodType && (
              <div className="text-red-500 text-xs">*{errors.bloodType}</div>
            )}
            {form.bloodType === "other" && (
              <div className="flex gap-2">
                <input
                  value={tempBloodType}
                  onChange={(e) => setTempBloodType(e.target.value)}
                  placeholder={
                    t("specifyBloodType", lang) || "Specify blood type..."
                  }
                  className="flex-1 border rounded p-2 text-sm"
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addBloodType())
                  }
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    addBloodType();
                  }}
                  className="px-3 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors"
                >
                  +
                </button>
              </div>
            )}

            {/* Display custom blood type if entered */}
            {form.otherBloodType && (
              <div className="flex items-center justify-between bg-[#f7f8fa] p-2 rounded text-sm">
                <span>{form.otherBloodType}</span>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      otherBloodType: "",
                      bloodType: "",
                    }))
                  }
                  className="text-red-500 hover:text-red-700 text-lg font-bold"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("dateOfBirth", lang)}
            </label>
            <DatePicker
              selected={form.dob ? new Date(form.dob) : null}
              onChange={handleDateChange}
              dateFormat="MM/dd/yyyy"
              placeholderText="MM/DD/YYYY"
              calendarClassName="custom-datepicker"
              popperPlacement="bottom"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              name="dob"
              autoComplete="off"
              customInput={<DateInput />}
              className={errors.dob ? "border-red-500" : ""}
            />
            {errors.dob && (
              <div className="text-red-500 text-xs">*{errors.dob}</div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("ssn", lang)}
            </label>
            {/* <input
              name="ssn"
              value={form.ssn}
              onChange={handleSSNChange}
              placeholder={t("ssn", lang) || "XXX-XX-XXXX"}
              className={`border rounded p-2 text-sm w-full ${
                errors.ssn ? "border-red-500" : ""
              }`}
              maxLength={11}
            /> */}
            <AccountInput
              value={form.ssn}
              name="ssn"
              onChange={handleSSNChange}
              placeholder={t("ssn", lang) || "XXX-XX-XXXX"}
              className={`border rounded p-2 text-sm w-full ${
                errors.ssn ? "border-red-500" : ""
              }`}
              mask="000-00-0000"
            />
            {errors.ssn && (
              <div className="text-red-500 text-xs">*{errors.ssn}</div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#2d348f]">
              {t("ekg", lang)}
            </label>
            <div className="flex flex-col gap-2">
              <label
                className={`border rounded p-2 text-sm w-full cursor-pointer flex items-center justify-between ${
                  errors.ekg ? "border-red-500" : "border-gray-300"
                } hover:border-[#4962d8] transition-colors`}
              >
                <span className="text-gray-600 truncate flex-1">
                  {ekgFileName || t("ekg", lang) || "Choose EKG file..."}
                </span>
                <span className="text-[#4962d8] font-medium ml-2 whitespace-nowrap">
                  {ekgFileName ? "Change File" : "Browse"}
                </span>
                <input
                  type="file"
                  name="ekg"
                  onChange={handleEkgFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                />
              </label>
              {ekgFileName && (
                <div className="flex items-center justify-between bg-gray-50 rounded p-2 text-xs">
                  <span className="text-gray-700 truncate flex-1">
                    {ekgFileName}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveEkgFile}
                    className="text-red-500 hover:text-red-700 ml-2 font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            {errors.ekg && (
              <div className="text-red-500 text-xs">*{errors.ekg}</div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Accepted formats: JPEG, PNG, GIF, WebP (Max 10MB)
            </p>
          </div>
          <button
            type="submit"
            className="bg-[#2d348f] text-white font-semibold py-2 rounded-lg shadow hover:bg-[#4962d8] transition-all mt-2 text-sm flex items-center justify-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              </>
            ) : (
              t("submit", lang)
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
